import { toast } from "react-toastify";

import { COMMON_SCHEMA, GLOBAL_SCHEMA } from "@/lib/zod";

import api from "../api";

type Options = {
  onInit?: () => void;
  onSuccess?: <T>(data: T) => void;
  onError?: <T>(error: T | unknown) => void;
  onFinally?: () => void;
};

type IdAndName = {
  id: number;
  name: string;
};

type IsTruck = {
  isTruck: boolean;
};
async function getIsTruck(plate: string, options?: Options) {
  const PLATE_SCHEMA = GLOBAL_SCHEMA.PLATE;
  const plateValidation = PLATE_SCHEMA.safeParse(plate);

  if (!plateValidation.success) {
    toast.error(plateValidation.error.errors[0].message);
    return [true, false] as const;
  }

  options?.onInit?.();
  try {
    const { data } = await api.get<IsTruck>(`truck/isTruck/${plate}`);
    const isTruck = data.isTruck;

    if (!isTruck) {
      throw new Error("A placa informada não corresponde a um caminhão!");
    }

    options?.onSuccess?.(data);

    return [false, isTruck] as const;
  } catch (_error) {
    options?.onError?.(_error);
    toast.error(_error instanceof Error ? _error.message : "Erro ao verificar se a placa corresponde a um caminhão.");
    console.error("Error checking if plate is a truck:", _error);
    return [true, false] as const;
  } finally {
    options?.onFinally?.();
    console.log("getIsTruck executed with plate: ", plate);
  }
}

type TruckBrand = IdAndName;
async function getBrands() {
  try {
    const { data } = await api.get<TruckBrand[]>("/truck-brand/");

    return [false, data] as const;
  } catch (_error) {
    toast.error("Erro ao buscar marcas de caminhões.");
    return [true, null] as const;
  } finally {
    console.log("getBrands executed");
  }
}

type TruckModel = IdAndName;
async function getModelsByBrand(brand: string, options?: Options) {
  const schema = COMMON_SCHEMA.PRIMITIVES.STRING.NOT_EMPTY;
  const brandValidation = schema.safeParse(brand);
  if (!brandValidation.success) {
    toast.error(brandValidation.error.errors[0].message);
    return [true, null] as const;
  }

  options?.onInit?.();

  try {
    const { data } = await api.get<TruckModel[]>(`/truck-model/brand/${brand}`);

    options?.onSuccess?.(data);

    return [false, data] as const;
  } catch (_error) {
    options?.onError?.(_error);
    toast.error("Erro ao buscar modelos de caminhões por marca.");
    console.error("Error fetching truck models by brand:", _error);
    return [true, null] as const;
  } finally {
    options?.onFinally?.();
    console.log("getModelsByBrand executed with brand: ", brand);
  }
}

async function getTruckById(id: string, options?: Options) {
  options?.onInit?.();

  try {
    const { data } = await api.get<NextApp.Truck>(`/truck/${id}`);
    options?.onSuccess?.(data);

    return [false, data] as const;
  } catch (_error) {
    options?.onError?.(_error);
    toast.error("Erro ao buscar caminhão pelo ID.");
    console.error("Error fetching truck by ID:", _error);
    return [true, null] as const;
  } finally {
    options?.onFinally?.();
  }
}

async function getTruckByDriverId(id: number) {
  try {
    const { data } = await api.get<Omit<NextApp.Truck, "company" | "driver" | "shippingCompany">[]>(`/truck/driver/${id}`);

    return { data, isSuccess: true, isError: false };
  } catch (error) {
    console.error("Error fetching truck by driver ID:", error);
    toast.error("Erro ao buscar caminhão pelo ID do motorista.");
    return { data: [], isSuccess: false, isError: true };
  } finally {
    console.log(`getTruckByDriverId executed with driver ID: ${id}`);
  }
}

export const truckQueries = {
  getModelsByBrand,
  getIsTruck,
  getBrands,
  getTruckById,
  getTruckByDriverId
};
